import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { Puzzle, Target, Clock, Trophy, TrendingUp, Zap } from 'lucide-react';
import { PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer } from 'recharts';
import { adminAPI } from '../services/api';
import StatCard from '../components/StatCard';
import { Card, CardHeader, CardTitle, CardContent } from '../components/Card';
import LoadingSpinner from '../components/LoadingSpinner';

const Puzzles = () => {
  const { data: stats, isLoading } = useQuery({
    queryKey: ['puzzleStats'],
    queryFn: adminAPI.getPuzzleStats,
  });

  if (isLoading) {
    return <LoadingSpinner text="Loading puzzle statistics..." />;
  }

  const COLORS = ['#FF5E9F', '#FFB101', '#00D2D3', '#FF4757', '#8B5CF6'];

  // Prepare data for attempt types pie chart
  const attemptTypeData = stats?.byType
    ? Object.entries(stats.byType).map(([type, data]) => ({
        name: type.charAt(0).toUpperCase() + type.slice(1),
        value: data.attempts,
        successRate: data.successRate
      }))
    : [];

  // Prepare rating distribution data
  const ratingData = stats?.ratingDistribution || [];

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Page Header */}
      <div>
        <h1 className="text-2xl sm:text-3xl font-bold text-text-primary">Puzzle Analytics</h1>
        <p className="text-sm sm:text-base text-text-secondary mt-1">View puzzle statistics and player performance</p>
      </div>

      {/* Overview Stats Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
        <StatCard
          title="Total Puzzles"
          value={stats?.overview?.totalPuzzles?.toLocaleString() || 0}
          icon={Puzzle}
          gradient={true}
        />
        <StatCard
          title="Total Attempts"
          value={stats?.overview?.totalAttempts?.toLocaleString() || 0}
          icon={Target}
        />
        <StatCard
          title="Success Rate"
          value={`${stats?.overview?.successRate || 0}%`}
          icon={TrendingUp}
        />
        <StatCard
          title="Avg Solve Time"
          value={`${stats?.overview?.avgSolveTimeSeconds || 0}s`}
          icon={Clock}
        />
      </div>

      {/* Secondary Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-6">
        <StatCard
          title="Attempts Today"
          value={stats?.overview?.attemptsToday?.toLocaleString() || 0}
          subtitle="puzzle attempts"
          icon={Zap}
        />
        <StatCard
          title="Rush Sessions"
          value={stats?.overview?.rushSessions?.toLocaleString() || 0}
          subtitle="total sessions played"
          icon={Trophy}
        />
        <StatCard
          title="Daily Completions"
          value={stats?.overview?.dailyCompletions?.toLocaleString() || 0}
          subtitle="daily puzzles solved"
          icon={Target}
        />
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Attempts by Type */}
        <Card>
          <CardHeader>
            <CardTitle>Attempts by Type</CardTitle>
          </CardHeader>
          <CardContent>
            {attemptTypeData.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={attemptTypeData}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={({ name, percent }) =>
                      `${name} ${(percent * 100).toFixed(0)}%`
                    }
                    outerRadius={100}
                    fill="#8884d8"
                    dataKey="value"
                  >
                    {attemptTypeData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip
                    contentStyle={{
                      backgroundColor: '#1A1A1F',
                      border: '1px solid #555555',
                      borderRadius: '8px',
                      color: '#FFFFFF',
                    }}
                    formatter={(value, name, props) => [
                      `${value} attempts (${props.payload.successRate}% success)`,
                      name
                    ]}
                  />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-[300px] flex items-center justify-center text-text-secondary">
                No puzzle attempt data available
              </div>
            )}
          </CardContent>
        </Card>

        {/* Puzzle Rating Distribution */}
        <Card>
          <CardHeader>
            <CardTitle>Puzzle Rating Distribution</CardTitle>
          </CardHeader>
          <CardContent>
            {ratingData.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={ratingData}>
                  <XAxis
                    dataKey="range"
                    stroke="#888"
                    tick={{ fill: '#888', fontSize: 12 }}
                  />
                  <YAxis
                    stroke="#888"
                    tick={{ fill: '#888', fontSize: 12 }}
                  />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: '#1A1A1F',
                      border: '1px solid #555555',
                      borderRadius: '8px',
                      color: '#FFFFFF',
                    }}
                    formatter={(value) => [`${value.toLocaleString()} puzzles`, 'Count']}
                  />
                  <Bar
                    dataKey="count"
                    fill="#FF5E9F"
                    radius={[4, 4, 0, 0]}
                  />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-[300px] flex items-center justify-center text-text-secondary">
                No rating distribution data available
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Top Themes */}
      <Card>
        <CardHeader>
          <CardTitle>Most Popular Puzzle Themes</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-background-elevated border-b border-border">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-text-secondary uppercase">
                    Rank
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-text-secondary uppercase">
                    Theme
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-text-secondary uppercase">
                    Puzzles
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-text-secondary uppercase">
                    Times Played
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-text-secondary uppercase">
                    Avg Rating
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {stats?.topThemes?.map((theme, index) => (
                  <tr key={theme.theme} className="hover:bg-background-elevated transition-colors">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-bold text-text-primary">#{index + 1}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-text-primary capitalize">
                        {theme.theme.replace(/([A-Z])/g, ' $1').trim()}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-text-primary">{theme.puzzleCount.toLocaleString()}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-text-primary">{theme.timesPlayed.toLocaleString()}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center gap-2">
                        <Trophy size={14} className="text-gold" />
                        <span className="text-sm font-semibold text-text-primary">
                          {theme.avgRating}
                        </span>
                      </div>
                    </td>
                  </tr>
                ))}
                {(!stats?.topThemes || stats.topThemes.length === 0) && (
                  <tr>
                    <td colSpan={5} className="px-6 py-8 text-center text-text-secondary">
                      No theme data available
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Top Solvers */}
      <Card>
        <CardHeader>
          <CardTitle>Top Puzzle Solvers</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-background-elevated border-b border-border">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-text-secondary uppercase">
                    Rank
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-text-secondary uppercase">
                    Player
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-text-secondary uppercase">
                    Puzzles Solved
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-text-secondary uppercase">
                    Avg Time
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-text-secondary uppercase">
                    Puzzle Rating
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {stats?.topSolvers?.map((solver, index) => (
                  <tr key={solver.id} className="hover:bg-background-elevated transition-colors">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className={`text-sm font-bold ${index === 0 ? 'text-gold' : index === 1 ? 'text-gray-400' : index === 2 ? 'text-amber-600' : 'text-text-primary'}`}>
                        #{index + 1}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-text-primary">
                        {solver.name}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-semibold text-success">{solver.puzzlesSolved.toLocaleString()}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center gap-2">
                        <Clock size={14} className="text-text-secondary" />
                        <span className="text-sm text-text-primary">
                          {solver.avgTimeSeconds}s
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center gap-2">
                        <Trophy size={14} className="text-gold" />
                        <span className="text-sm font-semibold text-text-primary">
                          {solver.puzzleRating}
                        </span>
                      </div>
                    </td>
                  </tr>
                ))}
                {(!stats?.topSolvers || stats.topSolvers.length === 0) && (
                  <tr>
                    <td colSpan={5} className="px-6 py-8 text-center text-text-secondary">
                      No solver data available
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Success Rate by Type */}
      {stats?.byType && Object.keys(stats.byType).length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Success Rate by Puzzle Type</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {Object.entries(stats.byType).map(([type, data]) => (
                <div key={type} className="bg-background-elevated rounded-lg p-4">
                  <div className="text-sm font-medium text-text-secondary capitalize mb-2">
                    {type}
                  </div>
                  <div className="text-2xl font-bold text-text-primary mb-1">
                    {data.successRate}%
                  </div>
                  <div className="text-xs text-text-muted">
                    {data.solved.toLocaleString()} / {data.attempts.toLocaleString()} solved
                  </div>
                  <div className="mt-2 h-2 bg-background rounded-full overflow-hidden">
                    <div
                      className="h-full bg-gradient-to-r from-primary to-gold transition-all"
                      style={{ width: `${data.successRate}%` }}
                    />
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default Puzzles;
